import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "../../utils/toast";
import { ArrowLeft, Loader2, Printer, X } from "lucide-react";

import {
  fetchPatientById,
  updatePatient,
} from "../../store/slices/patientSlice";
import { fetchVisits } from "../../store/slices/visitSlice";
import {
  fetchTestOrders,
  fetchTestOrderResults,
} from "../../store/slices/testOrderSlice";
import { fetchTests } from "../../store/slices/testSlice";

const PatientDetails = () => {
  const { id } = useParams();
  const patientId = Number(id);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Slices
  const {
    currentPatient,
    loading: patientLoading,
    error: patientError,
  } = useSelector((s) => s.patients || {});
  const { visits, loading: visitsLoading } = useSelector((s) => s.visits || {});
  const {
    testOrders,
    testOrderResults,
    loading: ordersLoading,
    error: ordersError,
  } = useSelector((s) => s.testOrders || {});
  const { tests, loading: testsLoading } = useSelector((s) => s.tests || {});

  // Local state
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [formData, setFormData] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [printAllForPatient, setPrintAllForPatient] = useState(false);

  // Fetch on mount
  useEffect(() => {
    if (!Number.isFinite(patientId)) return;
    dispatch(fetchPatientById(patientId));
    // Fetch all visits for this patient (no date filter to get history)
    dispatch(fetchVisits({ patient_id: patientId }));
    // Fetch all test orders then filter by this patient's visits client-side
    dispatch(fetchTestOrders({}));
    // Ensure tests are present to resolve test names
    dispatch(fetchTests());
  }, [dispatch, patientId]);

  // Initialize form when patient loads
  useEffect(() => {
    if (!currentPatient) return;
    setFormData({
      full_name: currentPatient.full_name || "",
      phone: currentPatient.phone || "",
      age: currentPatient.age || "",

      gender: currentPatient.gender || "",
      address: currentPatient.address || "",
      emergency_contact: currentPatient.emergency_contact || "",
      email: currentPatient.email || "",
      blood_type: currentPatient.blood_type || "unknown",
      known_allergies: currentPatient.known_allergies || "",
      chronic_conditions: currentPatient.chronic_conditions || "",
      current_medications: currentPatient.current_medications || "",
    });
  }, [currentPatient]);

  const visitsList = useMemo(
    () => (Array.isArray(visits) ? visits : visits?.data || []),
    [visits]
  );
  const ordersList = useMemo(
    () => (Array.isArray(testOrders) ? testOrders : testOrders?.data || []),
    [testOrders]
  );
  const testsById = useMemo(() => {
    const list = Array.isArray(tests) ? tests : tests?.data || [];
    return Object.fromEntries(list.map((t) => [t.id, t]));
  }, [tests]);

  // Build set of this patient's visit IDs
  const patientVisitIds = useMemo(() => {
    const set = new Set();
    (visitsList || []).forEach((v) => {
      if (v.patient_id === patientId) set.add(v.id);
    });
    return set;
  }, [visitsList, patientId]);

  // Orders belonging to this patient (orders with visit_id in patient's visits)
  const patientOrders = useMemo(
    () => (ordersList || []).filter((o) => patientVisitIds.has(o.visit_id)),
    [ordersList, patientVisitIds]
  );

  // Group orders by visit and sort recent first
  const ordersByVisit = useMemo(() => {
    const byVisit = new Map();
    patientOrders.forEach((o) => {
      const arr = byVisit.get(o.visit_id) || [];
      arr.push(o);
      byVisit.set(o.visit_id, arr);
    });
    // build entries with visit meta
    const entries = Array.from(byVisit.entries()).map(([visit_id, orders]) => {
      const v = visitsList.find((vv) => vv.id === visit_id);
      return {
        visit_id,
        visit_date: v?.visit_date,
        visit_time: v?.visit_time,
        status: v?.status,
        orders: orders.sort((a, b) =>
          String(b.ordered_at || "").localeCompare(String(a.ordered_at || ""))
        ),
      };
    });
    // sort by visit date/time desc
    return entries.sort((a, b) => {
      const aKey = `${a.visit_date || ""} ${a.visit_time || ""}`;
      const bKey = `${b.visit_date || ""} ${b.visit_time || ""}`;
      return bKey.localeCompare(aKey);
    });
  }, [patientOrders, visitsList]);

  const isBusy =
    patientLoading || visitsLoading || ordersLoading || testsLoading;

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/receptionist/patient-search");
  };

  const handleAssignTests = () => {
    if (!currentPatient) return;
    navigate("/receptionist/test-orders", {
      state: { patient: currentPatient, from: "patient-details" },
    });
  };

  const handleSave = async () => {
    if (!formData || !currentPatient?.id) return;
    try {
      const payload = {
        ...formData,
        // Keep date format acceptable by backend if it expects date string
        age: formData.age || null,
      };
      await dispatch(
        updatePatient({ id: currentPatient.id, patientData: payload })
      ).unwrap();
      toast.success("Patient updated successfully");
      setIsEditing(false);
      // refresh current patient
      dispatch(fetchPatientById(currentPatient.id));
    } catch (e) {
      const msg = typeof e === "string" ? e : "Failed to update patient";
      toast.error(msg);
    }
  };

  const openResults = async (order) => {
    try {
      setSelectedOrder(order);
      await dispatch(fetchTestOrderResults(order.id)).unwrap();
      setShowResults(true);
    } catch (e) {
      toast.error(typeof e === "string" ? e : "Failed to fetch results");
    }
  };

  const closeResults = () => {
    setSelectedOrder(null);
    setShowResults(false);
  };

  const resultsList = useMemo(() => {
    if (Array.isArray(testOrderResults)) return testOrderResults;
    return testOrderResults?.data || [];
  }, [testOrderResults]);

  const statusPill = (status) => {
    const map = {
      ordered: "bg-blue-100 text-blue-800",
      sample_collected: "bg-purple-100 text-purple-800",
      in_progress: "bg-orange-100 text-orange-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-gray-100 text-gray-700",
    };
    return `inline-flex items-center px-2 py-1 text-xs rounded-full ${
      map[status] || "bg-gray-100 text-gray-700"
    }`;
  };

  const formatTime = (t) => (!t ? "-" : String(t).slice(0, 5));

  // Prepare fields for rendering
  const cardNumber = currentPatient?.card_number || "-";
  const fullName = currentPatient?.full_name || "-";

  return (
    <div className="pt-6">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70">
          <div className="border-t-4 border-[#36F1A2] bg-gradient-to-r from-gray-50 to-white px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <button
                  type="button"
                  onClick={handleBack}
                  className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm font-semibold text-gray-700 ring-1 ring-gray-200 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
                <div className="mt-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Reception</div>
                  <h1 className="mt-1 truncate text-xl font-semibold text-gray-900">{fullName}</h1>
                  <p className="mt-1 text-sm text-gray-600">Card: {cardNumber}</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsEditing((v) => !v)}
                  className={
                    "inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2] " +
                    (isEditing
                      ? "bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
                      : "bg-[#36F1A2] text-[#235F72] hover:opacity-95")
                  }
                >
                  {isEditing ? "Cancel edit" : "Edit info"}
                </button>

                {!isEditing ? (
                  <button
                    type="button"
                    onClick={handleAssignTests}
                    className="inline-flex items-center justify-center rounded-xl bg-[#235F72] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                  >
                    Assign tests
                  </button>
                ) : null}
                {!isEditing ? (
                  <label className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 shadow-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-[#235F72] focus:ring-[#36F1A2]"
                      checked={printAllForPatient}
                      onChange={(e) => setPrintAllForPatient(e.target.checked)}
                    />
                    <span>Print all completed tests</span>
                  </label>
                ) : null}
              </div>
            </div>
          </div>

          <div className="space-y-6 px-5 py-5 sm:px-6">
            {patientError ? (
              <div className="rounded-2xl bg-red-50 p-4 text-sm text-red-700 ring-1 ring-red-200">
                {String(patientError)}
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              {[
                { id: "profile", label: "Profile" },
                { id: "history", label: "Test history" },
              ].map((tab) => {
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={
                      "rounded-full px-4 py-2 text-sm font-semibold ring-1 transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2] " +
                      (active
                        ? "bg-[#235F72] text-white ring-[#235F72]/20"
                        : "bg-gray-50 text-gray-700 ring-gray-200 hover:bg-gray-100")
                    }
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

      {/* Profile Tab */}
      {activeTab === "profile" && (
        <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200/70">
          {!formData ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-600">
              {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {isBusy ? "Loading patient…" : "No data"}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-semibold text-gray-900">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={formData.full_name}
                      onChange={(e) =>
                        setFormData({ ...formData, full_name: e.target.value })
                      }
                      disabled={!isEditing}
                      className="mt-2 w-full rounded-xl bg-white px-3.5 py-2.5 text-sm text-gray-900 ring-1 ring-gray-200 shadow-sm transition placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#36F1A2] disabled:bg-gray-100 disabled:text-gray-500"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-900">
                      Card Number
                    </label>
                    <input
                      type="text"
                      value={cardNumber}
                      disabled
                      className="mt-2 w-full rounded-xl bg-gray-100 px-3.5 py-2.5 text-sm text-gray-600 ring-1 ring-gray-200"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-900">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      disabled={!isEditing}
                      className="mt-2 w-full rounded-xl bg-white px-3.5 py-2.5 text-sm text-gray-900 ring-1 ring-gray-200 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2] disabled:bg-gray-100 disabled:text-gray-500"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-900">
                      Age
                    </label>
                    <input
                      type="number"
                      value={formData.age || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          age: e.target.value,
                        })
                      }
                      disabled={!isEditing}
                      className="mt-2 w-full rounded-xl bg-white px-3.5 py-2.5 text-sm text-gray-900 ring-1 ring-gray-200 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2] disabled:bg-gray-100 disabled:text-gray-500"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-900">
                      Gender
                    </label>
                    <select
                      value={formData.gender || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, gender: e.target.value })
                      }
                      disabled={!isEditing}
                      className="mt-2 w-full rounded-xl bg-white px-3.5 py-2.5 text-sm text-gray-900 ring-1 ring-gray-200 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2] disabled:bg-gray-100 disabled:text-gray-500"
                    >
                      <option value="">Select</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-900">
                      Blood Type
                    </label>
                    <select
                      value={formData.blood_type || "unknown"}
                      onChange={(e) =>
                        setFormData({ ...formData, blood_type: e.target.value })
                      }
                      disabled={!isEditing}
                      className="mt-2 w-full rounded-xl bg-white px-3.5 py-2.5 text-sm text-gray-900 ring-1 ring-gray-200 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2] disabled:bg-gray-100 disabled:text-gray-500"
                    >
                      {[
                        "A+",
                        "A-",
                        "B+",
                        "B-",
                        "AB+",
                        "AB-",
                        "O+",
                        "O-",
                        "unknown",
                      ].map((bt) => (
                        <option key={bt} value={bt}>
                          {bt}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-sm font-semibold text-gray-900">
                      Address
                    </label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) =>
                        setFormData({ ...formData, address: e.target.value })
                      }
                      disabled={!isEditing}
                      className="mt-2 w-full rounded-xl bg-white px-3.5 py-2.5 text-sm text-gray-900 ring-1 ring-gray-200 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2] disabled:bg-gray-100 disabled:text-gray-500"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-900">
                      Emergency Contact
                    </label>
                    <input
                      type="tel"
                      value={formData.emergency_contact}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          emergency_contact: e.target.value,
                        })
                      }
                      disabled={!isEditing}
                      className="mt-2 w-full rounded-xl bg-white px-3.5 py-2.5 text-sm text-gray-900 ring-1 ring-gray-200 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2] disabled:bg-gray-100 disabled:text-gray-500"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-900">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      disabled={!isEditing}
                      className="mt-2 w-full rounded-xl bg-white px-3.5 py-2.5 text-sm text-gray-900 ring-1 ring-gray-200 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2] disabled:bg-gray-100 disabled:text-gray-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-sm font-semibold text-gray-900">
                      Known Allergies
                    </label>
                    <input
                      type="text"
                      value={formData.known_allergies}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          known_allergies: e.target.value,
                        })
                      }
                      disabled={!isEditing}
                      className="mt-2 w-full rounded-xl bg-white px-3.5 py-2.5 text-sm text-gray-900 ring-1 ring-gray-200 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2] disabled:bg-gray-100 disabled:text-gray-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-sm font-semibold text-gray-900">
                      Chronic Conditions
                    </label>
                    <input
                      type="text"
                      value={formData.chronic_conditions}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          chronic_conditions: e.target.value,
                        })
                      }
                      disabled={!isEditing}
                      className="mt-2 w-full rounded-xl bg-white px-3.5 py-2.5 text-sm text-gray-900 ring-1 ring-gray-200 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2] disabled:bg-gray-100 disabled:text-gray-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-sm font-semibold text-gray-900">
                      Current Medications
                    </label>
                    <input
                      type="text"
                      value={formData.current_medications}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          current_medications: e.target.value,
                        })
                      }
                      disabled={!isEditing}
                      className="mt-2 w-full rounded-xl bg-white px-3.5 py-2.5 text-sm text-gray-900 ring-1 ring-gray-200 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2] disabled:bg-gray-100 disabled:text-gray-500"
                    />
                  </div>
                </div>

                {isEditing && (
                  <div className="mt-6 flex justify-end gap-3 border-t border-gray-100 pt-6">
                    <button
                      onClick={() => setIsEditing(false)}
                      className="inline-flex items-center justify-center rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 ring-1 ring-gray-200 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={patientLoading}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#235F72] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#36F1A2] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {patientLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      {patientLoading ? "Saving…" : "Save changes"}
                    </button>
                  </div>
                )}
            </>
          )}
        </div>
      )}

      {/* Test History Tab */}
      {activeTab === "history" && (
        <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200/70">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900">Laboratory test history</h2>
              <p className="mt-0.5 text-xs text-gray-600">Visits and related test orders for this patient.</p>
            </div>
          </div>

          {ordersError ? (
            <div className="mt-4 rounded-2xl bg-red-50 p-4 text-sm text-red-700 ring-1 ring-red-200">
              {String(ordersError)}
            </div>
          ) : null}

          {isBusy ? (
            <div className="mt-4 flex items-center justify-center gap-2 py-12 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading history…
            </div>
          ) : ordersByVisit.length === 0 ? (
            <div className="mt-4 rounded-2xl bg-gray-50 px-4 py-12 text-center text-sm text-gray-600 ring-1 ring-gray-200/70">
              No test history available.
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {ordersByVisit.map((item) => (
                <div key={item.visit_id} className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200/70">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="text-sm font-semibold text-gray-900">
                          {item.visit_date
                            ? new Date(item.visit_date).toLocaleDateString()
                            : "Visit"}
                          {item.visit_time ? ` • ${formatTime(item.visit_time)}` : ""}
                        </div>
                        {item.status ? (
                          <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 ring-1 ring-gray-200">
                            {String(item.status).replace(/_/g, " ")}
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-0.5 text-xs text-gray-600">Visit ID: {item.visit_id}</div>
                    </div>
                  </div>

                  <div className="mt-3 overflow-hidden rounded-2xl bg-white ring-1 ring-gray-200">
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                            <th className="px-4 py-3">Test</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Payment</th>
                            <th className="px-4 py-3">Priority</th>
                            <th className="px-4 py-3">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {item.orders.map((o) => {
                            const testName =
                              testsById[o.test_id]?.name || `Test #${o.test_id}`;
                            const completed = o.status === "completed";
                            return (
                              <tr key={o.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3">
                                  <div className="font-semibold text-gray-900">{testName}</div>
                                  {o.ordered_at ? (
                                    <div className="mt-0.5 text-xs text-gray-600">
                                      Ordered: {new Date(o.ordered_at).toLocaleString()}
                                    </div>
                                  ) : null}
                                </td>
                                <td className="px-4 py-3">
                                  <span className={statusPill(o.status)}>
                                    {String(o.status || "").replace(/_/g, " ")}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <span
                                    className={
                                      "rounded-full px-2.5 py-1 text-xs font-semibold ring-1 " +
                                      (String(o.payment_status || "unpaid") === "paid"
                                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                                        : String(o.payment_status || "unpaid") === "partial"
                                        ? "bg-amber-50 text-amber-800 ring-amber-200"
                                        : String(o.payment_status || "unpaid") === "refunded"
                                        ? "bg-slate-100 text-slate-700 ring-slate-200"
                                        : "bg-rose-50 text-rose-700 ring-rose-200")
                                    }
                                  >
                                    {String(o.payment_status || "unpaid").replace(/_/g, " ")}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  {o.priority ? (
                                    <span
                                      className={
                                        "rounded-full px-2.5 py-1 text-xs font-semibold ring-1 " +
                                        (o.priority === "urgent"
                                          ? "bg-red-50 text-red-700 ring-red-200"
                                          : "bg-gray-100 text-gray-800 ring-gray-200")
                                      }
                                    >
                                      {o.priority}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-gray-500">—</span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <button
                                      type="button"
                                      disabled={!completed}
                                      onClick={() => openResults(o)}
                                      className={
                                        completed
                                          ? "rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-[#085DB6] ring-1 ring-gray-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                                          : "cursor-not-allowed rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-400 ring-1 ring-gray-200"
                                      }
                                    >
                                      View results
                                    </button>
                                    <button
                                      type="button"
                                      disabled={!completed}
                                      onClick={() => {
                                        if (printAllForPatient) {
                                          const completedOrders = patientOrders.filter(
                                            (po) => String(po.status || "") === "completed"
                                          );
                                          const ids = completedOrders.map((po) => po.id).join(",");
                                          if (!ids) {
                                            toast.warn("No completed orders to print for this patient.");
                                            return;
                                          }
                                          navigate(`/print-report?orderIds=${encodeURIComponent(ids)}`);
                                        } else {
                                          navigate(`/print-report?orderId=${o.id}`);
                                        }
                                      }}
                                      className={
                                        completed
                                          ? "inline-flex items-center gap-2 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-[#235F72] ring-1 ring-gray-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                                          : "inline-flex cursor-not-allowed items-center gap-2 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-400 ring-1 ring-gray-200"
                                      }
                                    >
                                      <Printer className="h-4 w-4" />
                                      Print
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Results Modal */}
      {showResults && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-gray-200">
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Test results</div>
                <div className="mt-0.5 text-xs text-gray-600">Order #{selectedOrder?.id}</div>
              </div>
              <button
                type="button"
                onClick={closeResults}
                className="inline-flex items-center justify-center rounded-xl bg-white p-2 text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="max-h-[70vh] space-y-3 overflow-y-auto px-5 py-4">
              {ordersLoading ? (
                <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading results…
                </div>
              ) : resultsList.length === 0 ? (
                <div className="rounded-2xl bg-gray-50 px-4 py-12 text-center text-sm text-gray-600 ring-1 ring-gray-200/70">
                  No results available.
                </div>
              ) : (
                resultsList.map((r) => (
                  <div key={r.id} className="rounded-2xl bg-white p-4 ring-1 ring-gray-200">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-gray-700">
                          Value:{" "}
                          <span className="font-semibold text-gray-900">
                            {r.result_value || r.numeric_value || "-"}
                          </span>{" "}
                          {r.unit ? <span className="text-gray-500">{r.unit}</span> : null}
                        </div>

                        {r.comments ? (
                          <div className="mt-1 text-xs text-gray-600">Comments: {r.comments}</div>
                        ) : null}
                        {r.interpretation ? (
                          <div className="mt-1 text-xs text-gray-600">Interpretation: {r.interpretation}</div>
                        ) : null}

                        <div className="mt-2 text-xs text-gray-500">
                          Verified: {r.is_verified ? "Yes" : "No"}
                          {r.verified_at ? ` (${new Date(r.verified_at).toLocaleString()})` : ""}
                        </div>
                      </div>

                      <span
                        className={
                          "shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 " +
                          (r.status === "high"
                            ? "bg-red-50 text-red-700 ring-red-200"
                            : r.status === "low"
                            ? "bg-yellow-50 text-yellow-800 ring-yellow-200"
                            : r.status === "critical"
                            ? "bg-purple-50 text-purple-800 ring-purple-200"
                            : "bg-green-50 text-green-700 ring-green-200")
                        }
                      >
                        {r.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-4">
              <button
                type="button"
                onClick={closeResults}
                className="inline-flex items-center justify-center rounded-xl bg-[#235F72] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDetails;
